'use client';
import { createContentfulPersister } from '@/lib/contentful/persister';
import { removeFromLocalStorage } from '@/lib/local-storage/local-storage';
import { typeGuard } from '@/lib/utils';
import { DEFAULT_STALE_TIME } from '@/requests/constants';
import {
  isServer,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { useEffect } from 'react';
import { SESSION_KEY } from './session-provider/constants';

declare global {
  interface Window {
    queryClient?: QueryClient;
  }
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { skipGlobalLogoutOn401?: boolean };
    mutationMeta: { skipGlobalLogoutOn401?: boolean };
  }
}

export const NO_RETRY_CODES = [400, 401, 402, 403, 404, 405, 429] as const;
export const NO_RETRY_SET = new Set<number>(NO_RETRY_CODES);

export function isStatusError(error: unknown): error is { status: number } {
  return typeGuard(error, 'status') && typeof error.status === 'number';
}

function makeQueryClient(logout?: () => void) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        retryDelay: (attemptIndex) => {
          const exponentialDelay = Math.min(1000 * 2 ** attemptIndex, 30000);
          const jitter = 0.5 + Math.random() * 0.5;
          return Math.floor(exponentialDelay * jitter);
        },
        retry(failureCount, error) {
          if (isStatusError(error) && NO_RETRY_SET.has(error.status)) {
            return false;
          }
          return failureCount < 3;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Some queries (e.g. probing other stored sessions' tokens) expect a
        // 401 and must not sign the active user out.
        if (query.meta?.skipGlobalLogoutOn401) {
          return;
        }
        if (isStatusError(error) && error.status === 401) {
          logout?.();
        }
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient(logout?: () => void) {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient(logout);
    return browserQueryClient;
  }
}

/** 4 hours in milliseconds - matches the Contentful hooks staleTime */
const CONTENTFUL_MAX_AGE = 1000 * 60 * 60 * 4;

export default function Provider({ children }: { children: React.ReactNode }) {
  function logout() {
    clear();
  }

  function clear() {
    queryClient.cancelQueries();
    queryClient.clear();
    removeFromLocalStorage({ key: SESSION_KEY });
    // Note: We intentionally don't clear Contentful cache on logout
    // since release notes are the same for all users
  }

  const queryClient = getQueryClient(logout);
  if (typeof window !== 'undefined' && !window.queryClient) {
    window.queryClient = queryClient;
  }

  // Set up Contentful cache persistence
  useEffect(() => {
    const persister = createContentfulPersister();
    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
      maxAge: CONTENTFUL_MAX_AGE,
      // Only persist queries that start with 'contentful' in the query key
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => query.queryKey[0] === 'contentful',
      },
    });

    return unsubscribe;
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* <ReactQueryDevtools position='bottom' initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
}
